import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal, Radio, RadioChangeEvent, Space } from 'antd';

import { FamilyMember, useProfileImage } from 'all-in-one-shared-api';
import { useCachedClaimProperties } from 'hooks';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
import { PrimaryButton } from 'components';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { Avatar } from 'components/avatar';

export const MakeBookingButton = (): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [patients, setPatients] = useState<FamilyMember[] | undefined>([]);
  const [selectedPatient, setSelectedPatient] = useState<string>();
  const [memberTypes, setMemberTypes] = useState<string>();
  const [profileImageMap, setProfileImageMap] = useState<Map<string, string | undefined>>();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, tokenParsed } = useKeycloakReady();

  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { data: claimProperties } = useCachedClaimProperties(claimPropertiesKey);

  const currentMemberType = tokenParsed ? tokenParsed.memberType : '';

  const {
    shouldFetch: fetchProfileImage,
    data: profileImageData,
    error: profileImageError,
  } = useProfileImage({ name: 'getProfileImage', memberTypes }, token ?? '', memberTypes);

  useEffect(() => {
    if (claimProperties?.familyMembers?.length) {
      const { familyMembers } = claimProperties;
      setPatients(familyMembers);

      setSelectedPatient(familyMembers.find((member) => member.memberType === currentMemberType)?.memberType);
      setMemberTypes(familyMembers?.map((member) => member.memberType).join(','));
    }
  }, [claimProperties, claimProperties?.familyMembers?.length, currentMemberType]);

  useEffect(() => {
    if (memberTypes) {
      fetchProfileImage(true);
    }
  }, [memberTypes, fetchProfileImage]);

  useEffect(() => {
    if (profileImageData && !profileImageError) {
      const profileImageMap = new Map<string, string | undefined>();
      profileImageData?.forEach((data) => {
        if (data.memberType) {
          profileImageMap.set(data.memberType, data.pictureUrl);
        }
      });
      setProfileImageMap(profileImageMap);
    }
  }, [profileImageData, profileImageError]);

  const handleOkButton = () => {
    setIsModalOpen(false);
    navigate('/teladoc-booking/disclaimer', {
      state: claimProperties?.familyMembers?.find((member) => member.memberType === selectedPatient),
    });
  };

  return (
    <>
      <PrimaryButton disabled={patients?.length === 0} onClick={() => setIsModalOpen(true)}>
        {t('teladoc.makeABooking')}
      </PrimaryButton>
      <Modal
        title={t('teladoc.selectPatientHeading')}
        open={isModalOpen}
        okText={t('common.next')}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={handleOkButton}
        onCancel={() => setIsModalOpen(false)}
      >
        {patients && patients?.length > 0 && (
          <Radio.Group value={selectedPatient} onChange={(e: RadioChangeEvent) => setSelectedPatient(e.target.value)}>
            <Space direction="vertical">
              {patients.map((patient) => {
                const avatarUrl = profileImageMap?.get(patient.memberType ?? '');
                return (
                  <Radio key={patient.memberType} value={patient.memberType}>
                    <Space>
                      <Avatar avatarUrl={avatarUrl} />
                      {`${patient.firstName} ${patient.lastName}`}
                    </Space>
                  </Radio>
                );
              })}
            </Space>
          </Radio.Group>
        )}
      </Modal>
    </>
  );
};
